import { Component, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CustomerSelectionService } from '../../services/customer-selection';
import { CartService } from '../../services/cart';
import { Customer } from '../../services/customer';
import { ProductService, SelectedProduct, normalizeSaleItems } from '../../services/products';
import { CartComponent } from '../../components/cart-component/cart-component';
import { v4 as uuidv4 } from 'uuid';
import { CreateSalesOrderPayload, SalesOrderService } from '../../services/sales-order';
import { MessageDialogService } from '../../services/message-dialog';
import { NotificationService } from '../../services/notification';
import { Router } from '@angular/router';
import { PendingOrderService, SalesOrderState } from '../../services/pending-order-service';

@Component({
  selector: 'app-place-order',
  templateUrl: './place-order-component.html',
  styleUrls: ['./place-order-component.scss'],
  standalone: true,
  imports: [
    CartComponent
  ]
})
export class PlaceOrderComponent implements OnInit {
  selectedCustomer: Customer | null = null;
  selectedProducts: SelectedProduct[] = [];

  constructor(
    private customerService: CustomerSelectionService,
    private cartService: CartService,
    private salesOrderService: SalesOrderService,
    private messageDialog: MessageDialogService,
    private notificationService: NotificationService,
    private pendingOrderService: PendingOrderService,
    private productService: ProductService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.selectedCustomer = this.customerService.getSelected();

    // Initialize cart
    this.cartService.cartChanges$.subscribe(cart => {
      this.selectedProducts = cart;
    });
  }

  onCartUpdated(updatedCart: SelectedProduct[]) {
    this.selectedProducts = updatedCart;
    this.cartService.updateCart(updatedCart);
  }

  handleOrderPlaced() {
    console.log('Order successfully placed from PlaceOrderComponent');
    // You can also navigate or update any shared state here
  }

  private async normalizePrices(items: SelectedProduct[]): Promise<SelectedProduct[]> {
    const products = await firstValueFrom(this.productService.get());
    return normalizeSaleItems(items, products);
  }

  editOrder(): void {
    if (!this.selectedCustomer) {
      console.warn('No customer selected');
      return;
    }
    this.router.navigate(['/order']);
  }

  async saveAsDraft(): Promise<void> {
    if (!this.selectedCustomer) {
      console.warn('No customer selected');
      return;
    }

    let validProducts = this.selectedProducts.filter(p => p.quantity > 0);

    if (validProducts.length === 0) {
      this.messageDialog.error('Please select at least one product with quantity greater than zero.');
      return;
    }

    validProducts = await this.normalizePrices(validProducts);

    const payload: CreateSalesOrderPayload = {
      partner_id: this.selectedCustomer.id,
      partner_client_uuid: this.selectedCustomer.clientCustomerUuid || undefined,
      idempotencyKey: `order-${Date.now()}-${uuidv4()}`,
      order_line: validProducts.map(p => ({
        product_id: p.productId,
        product_uom_qty: p.quantity,
        price_unit: p.price
      }))
    };

    await this.pendingOrderService.savePendingOrder(payload, SalesOrderState.DRAFT);
    this.notificationService.showMessage('Order saved as draft');
    this.cartService.clearCart();
    this.router.navigate(['/customers']);
  }
  

  async placeOrder(): Promise<void> {
    if (!this.selectedCustomer) {
      console.warn('No customer selected');
      return;
    }

    let validProducts = this.selectedProducts.filter(p => p.quantity > 0);

    if (validProducts.length === 0) {
      this.messageDialog.error('Please select at least one product with quantity greater than zero.');
      return;
    }


    validProducts = await this.normalizePrices(validProducts);
    const payload: CreateSalesOrderPayload = {
      partner_id: this.selectedCustomer.id,
      partner_client_uuid: this.selectedCustomer.clientCustomerUuid || undefined,
      idempotencyKey: `order-${Date.now()}-${uuidv4()}`,
      order_line: validProducts.map(p => ({
        product_id: p.productId,
        product_uom_qty: p.quantity,
        price_unit: p.price
      }))
    };

    if (this.selectedCustomer.pendingSync || this.selectedCustomer.id <= 0) {
      await this.pendingOrderService.savePendingOrder(payload, SalesOrderState.SENT);
      this.notificationService.showMessage('Customer is pending sync. Order saved to pending orders.');
      await this.cartService.clearCart();
      this.router.navigate(['/customers']);
      return;
    }

    this.salesOrderService.createOrder(payload).subscribe({
      next: response => {
        if (response.status === 'success') {
          this.notificationService.showMessage('Order placed successfully');
          this.cartService.clearCart(); 
          this.router.navigate(['/customers']);
        } else {
          this.messageDialog.error(response.message || 'Order creation failed');
        }
      },
      error: async err => {
        const serverMessage = err?.error?.message || err?.message || 'Unexpected error occurred while creating the order.';
        console.error(serverMessage);

        if (err?.status === 400 && err?.error?.code === 'minimum_price_violation') {
          this.messageDialog.error('Product pricing changed. Sync products and try the order again.');
          return;
        }

        await this.pendingOrderService.savePendingOrder(payload, SalesOrderState.SENT);
        this.notificationService.showMessage('Unable to place order, moved to pending orders');
        this.cartService.clearCart(); 
        this.router.navigate(['/customers']);
      }
    });
  }
}
